import type { NoteName } from '../../utils/musicTheory';
import type { SessionMode } from '../types/module';
import {
  recordLearnModuleProgress,
  type LearnModuleId,
} from '../learn/learnStore';
import { recordAttempt } from './progressStore';

/** Open-ended sessions (jams, tabs) shorter than this aren't worth logging. */
export const MIN_LOGGED_PRACTICE_SECONDS = 30;

interface PracticeRunBase {
  moduleId: LearnModuleId;
  mode: SessionMode;
  title: string;
  key?: NoteName;
  scaleName?: string;
  startedAtMs: number;
  endedAtMs?: number;
  bpm?: number;
  focus?: string;
  milestone?: string;
  metadata?: Record<string, unknown>;
  /** Merged into the module's saved config; defaults to `metadata`. */
  resumeConfig?: Record<string, unknown>;
}

export interface CompletedPracticeRun extends PracticeRunBase {
  notesTotal: number;
  notesMissed?: number;
}

/** Everything about an open-ended session except its timing. */
export type PracticeSessionDetails = Omit<
  PracticeRunBase,
  'startedAtMs' | 'endedAtMs'
>;

function durationSecondsOf(run: PracticeRunBase, endedAtMs: number): number {
  return Math.max(1, Math.round((endedAtMs - run.startedAtMs) / 1000));
}

function logAttempt(
  run: PracticeRunBase,
  endedAtMs: number,
  durationSeconds: number,
  notes: { total: number; missed: number }
): void {
  recordAttempt({
    id: `${run.moduleId}-${run.startedAtMs}-${endedAtMs}`,
    templateId: run.moduleId,
    mode: run.mode,
    title: run.title,
    key: run.key,
    scaleName: run.scaleName,
    startedAt: new Date(run.startedAtMs).toISOString(),
    endedAt: new Date(endedAtMs).toISOString(),
    durationSeconds,
    bpm: run.bpm ?? 0,
    completed: true,
    clean: notes.missed === 0,
    notesHit: notes.total,
    notesMissed: notes.missed,
    notesTotal: notes.total,
    accuracy:
      notes.total > 0
        ? Math.round((notes.total / (notes.total + notes.missed)) * 100)
        : 100,
    metadata: run.metadata,
  });
}

/** Logs a finished exercise to dashboard stats and Learn progress together. */
export function recordPracticeRun(run: CompletedPracticeRun): void {
  const endedAtMs = run.endedAtMs ?? Date.now();
  const durationSeconds = durationSecondsOf(run, endedAtMs);
  logAttempt(run, endedAtMs, durationSeconds, {
    total: run.notesTotal,
    missed: run.notesMissed ?? 0,
  });
  recordLearnModuleProgress(run.moduleId, {
    completed: true,
    durationSeconds,
    focus: run.focus,
    milestone: run.milestone,
    metadata: run.resumeConfig ?? run.metadata,
  });
}

/**
 * Logs time spent in an open-ended session (guided solo, progressions, tabs).
 * Returns false when the session was too short to count.
 */
export function recordPracticeTime(run: PracticeRunBase): boolean {
  const endedAtMs = run.endedAtMs ?? Date.now();
  const durationSeconds = durationSecondsOf(run, endedAtMs);
  if (durationSeconds < MIN_LOGGED_PRACTICE_SECONDS) return false;
  logAttempt(run, endedAtMs, durationSeconds, { total: 0, missed: 0 });
  recordLearnModuleProgress(run.moduleId, {
    completed: true,
    durationSeconds,
    focus: run.focus,
    metadata: run.resumeConfig ?? run.metadata,
  });
  return true;
}
